import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, ClipboardList, Search, Users } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { translateTrafficLight } from '../../utils/i18nHelpers';

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function trafficDotClass(light) {
  const key = String(light || '').toLowerCase();
  if (key === 'green') return 'bg-emerald-500';
  if (key === 'yellow') return 'bg-amber-400';
  return 'bg-red-500';
}

function trafficCardClass(light) {
  const key = String(light || '').toLowerCase();
  if (key === 'green') return 'border-emerald-200 bg-emerald-50';
  if (key === 'yellow') return 'border-amber-200 bg-amber-50';
  return 'border-red-200 bg-red-50';
}

/** Panel del entrenador — Mis asesorados (paridad visual con FOOD PLAN /trainer). */
export default function CoachAdviseesPanel({ onBack, onNavigate }) {
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, lightRes] = await Promise.all([
        api.get('/training/coach/clients'),
        api.get('/training/progress/traffic-light/clients').catch(() => ({ data: { data: [] } })),
      ]);
      const stats = statsRes.data?.data || [];
      const lights = lightRes.data?.data || [];
      const lightById = new Map(lights.map((l) => [Number(l.user_id), l]));
      const merged = stats.map((c) => {
        const light = lightById.get(Number(c.id));
        return {
          ...c,
          traffic_light: light?.traffic_light || 'red',
          adherence_pct: light?.adherence_pct ?? c.adherence ?? 0,
        };
      });
      setClients(merged);
    } catch {
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onFocus = () => { load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') load();
    });
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const counts = useMemo(() => {
    const tally = { total: clients.length, green: 0, yellow: 0, red: 0 };
    for (const c of clients) {
      const key = String(c.traffic_light || 'red').toLowerCase();
      if (key === 'green') tally.green += 1;
      else if (key === 'yellow') tally.yellow += 1;
      else tally.red += 1;
    }
    return tally;
  }, [clients]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      const light = String(c.traffic_light || 'red').toLowerCase();
      if (filter === 'promo') {
        if (!c.training_promo?.code) return false;
      } else if (filter !== 'all' && light !== filter) {
        return false;
      }
      if (!q) return true;
      return String(c.nombre || '').toLowerCase().includes(q)
        || String(c.email || '').toLowerCase().includes(q);
    });
  }, [clients, search, filter]);

  const openClientTracking = (clientId) => {
    try {
      sessionStorage.setItem('coach.tracking.selectedUserId', String(clientId));
    } catch { /* noop */ }
    onNavigate?.('progress-training');
  };

  return (
    <div className="dashboard-main-view training-coach-view training-light-island surface-readable d28d-admin-surface space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {onBack && (
            <button type="button" className="btn-secondary text-sm mb-3" onClick={onBack}>
              ← {t('common.back', 'Volver')}
            </button>
          )}
          <h1 className="d28d-page-title text-2xl">
            {t('coach.panel.title', 'Panel del Entrenador')}
          </h1>
          <p className="d28d-text-muted text-sm mt-1">
            {t('coach.panel.subtitle', 'Mis asesorados — seguimiento, planes y adherencia.')}
          </p>
        </div>
        <button type="button" className="btn-secondary text-sm" onClick={load} disabled={loading}>
          {loading ? t('common.loading', 'Cargando…') : t('common.refresh', 'Actualizar')}
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label={t('coach.panel.total', 'Total')} value={counts.total} />
        <StatCard
          label={t('d28d.progress.traffic.green', 'Verde')}
          value={counts.green}
          className="border-emerald-200 bg-emerald-50/80"
          valueClass="text-emerald-800"
        />
        <StatCard
          label={t('d28d.progress.traffic.yellow', 'Amarillo')}
          value={counts.yellow}
          className="border-amber-200 bg-amber-50/80"
          valueClass="text-amber-900"
        />
        <StatCard
          label={t('d28d.progress.traffic.red', 'Rojo')}
          value={counts.red}
          className="border-red-200 bg-red-50/80"
          valueClass="text-red-800"
        />
      </div>

      <section className="training-light-island surface-readable rounded-xl border border-slate-200 p-4 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" aria-hidden />
            <input
              type="search"
              className="input w-full pl-9"
              placeholder={t('coach.panel.search', 'Buscar por nombre o correo…')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="input w-auto min-w-[140px]" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">{t('coach.panel.filter_all', 'Todos')}</option>
            <option value="promo">Con promoción</option>
            <option value="green">{translateTrafficLight('green', t)}</option>
            <option value="yellow">{translateTrafficLight('yellow', t)}</option>
            <option value="red">{translateTrafficLight('red', t)}</option>
          </select>
        </div>

        {loading && (
          <p className="text-sm text-stone-500 py-8 text-center">{t('common.loading', 'Cargando…')}</p>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 border border-dashed border-slate-300 rounded-xl">
            <Users className="w-10 h-10 mx-auto text-stone-400 mb-3" aria-hidden />
            <p className="text-stone-700 font-medium">
              {t('coach.panel.empty', 'Aún no tienes asesorados activos')}
            </p>
            <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto">
              {t('coach.panel.empty_hint', 'Cuando actives clientes desde «Activar clientes», aparecerán aquí con su semáforo de adherencia.')}
            </p>
            <button
              type="button"
              className="btn-primary mt-4"
              onClick={() => onNavigate?.('training-coach-pay')}
            >
              {t('coach.panel.go_activate', 'Ir a Activar clientes')}
            </button>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <ul className="space-y-3">
            {filtered.map((c) => (
              <li
                key={c.id}
                className={`rounded-xl border p-4 ${trafficCardClass(c.traffic_light)}`}
              >
                <div className="flex flex-wrap items-start gap-4">
                  <div className="relative shrink-0">
                    <div className="w-12 h-12 rounded-full bg-white border border-stone-200 grid place-items-center font-bold text-stone-800">
                      {initials(c.nombre)}
                    </div>
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${trafficDotClass(c.traffic_light)}`}
                      title={translateTrafficLight(c.traffic_light, t)}
                    />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <h3 className="font-bold text-stone-900 inline-flex flex-wrap items-center gap-2">
                      {c.nombre}
                      {c.training_promo?.code ? (
                        <span className="inline-flex items-center rounded-full bg-violet-100 text-violet-900 text-xs font-semibold px-2 py-0.5">
                          {c.training_promo.label || c.training_promo.code}
                        </span>
                      ) : null}
                    </h3>
                    <p className="text-sm text-stone-600">{c.email}</p>
                    <p className="text-xs text-stone-500 mt-1">
                      {c.has_plan
                        ? t('coach.panel.with_plan', 'Con plan asignado')
                        : t('coach.panel.no_plan', 'Sin plan')}
                      {' · '}
                      {c.adherence_pct ?? 0}% {t('coach.panel.adherence', 'adherencia')}
                      {' · '}
                      {translateTrafficLight(c.traffic_light, t)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-secondary text-xs inline-flex items-center gap-1"
                      onClick={() => openClientTracking(c.id)}
                    >
                      <Activity className="w-3.5 h-3.5" aria-hidden />
                      {t('nav.tracking', 'Seguimiento')}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary text-xs inline-flex items-center gap-1"
                      onClick={() => {
                        try {
                          sessionStorage.setItem('coach.planning.selectedUserId', String(c.id));
                        } catch { /* noop */ }
                        onNavigate?.('admintraining');
                      }}
                    >
                      <ClipboardList className="w-3.5 h-3.5" aria-hidden />
                      {t('nav.edit_routines', 'Editar rutina')}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, className = '', valueClass = 'text-stone-900' }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}>
      <p className="text-xs uppercase tracking-wide text-stone-500 font-semibold">{label}</p>
      <p className={`text-3xl font-extrabold mt-1 ${valueClass}`}>{value}</p>
    </div>
  );
}
